import type { Currency } from '@chukua/common';
import { Alert, DatePicker, Form, Input, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { FollowUpSearchSelect } from '../components/FollowUpSearchSelect';
import { InvoiceSearchSelect, type InvoiceChoice } from '../components/InvoiceSearchSelect';
import { useSavePromise } from '../hooks/use-save-promise';
import { toPromiseInput } from '../policies/promise-form';

interface RecordPromiseModalProps {
  open: boolean;
  customerId: string;
  /** The follow-up the promise was made in, when the caller knows it (the row action); otherwise the form asks. */
  followUpId?: string;
  /** Set to replace this open promise: the new one keeps it on the list as superseded. */
  supersedesPromiseId?: string;
  onSaved?: () => void;
  onClose: () => void;
}

interface FormValues {
  followUpId?: string;
  promisedDate: Dayjs;
  amount: string;
  invoices: InvoiceChoice[];
}

/** A promise is evidence, never edited: a changed date or amount is a new promise that supersedes the open one. */
export function RecordPromiseModal({ open, customerId, followUpId, supersedesPromiseId, onSaved, onClose }: RecordPromiseModalProps) {
  const [form] = Form.useForm<FormValues>();
  const { error, saving, save } = useSavePromise(customerId, supersedesPromiseId ?? null);
  const invoices = Form.useWatch('invoices', form) ?? [];
  const currency: Currency | null = invoices[0]?.currency ?? null;

  async function submit(values: FormValues) {
    const madeIn = followUpId ?? values.followUpId;
    if (!madeIn) return;
    const input = toPromiseInput(
      {
        followUpId: madeIn,
        promisedDate: values.promisedDate.format('YYYY-MM-DD'),
        amount: values.amount,
        invoiceIds: values.invoices.map((invoice) => invoice.value),
      },
      values.invoices[0].currency,
    );
    if (!input) return form.setFields([{ name: 'amount', errors: ['Enter an amount greater than zero'] }]);
    if (await save(input)) {
      onSaved?.();
      onClose();
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={supersedesPromiseId ? 'Replace promise' : 'Record promise'}
      okButtonProps={{ loading: saving }}
      title={supersedesPromiseId ? 'Replace a promise to pay' : 'Record a promise to pay'}
      destroyOnHidden
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={saving}>
        {!followUpId && (
          <Form.Item name="followUpId" label="Made in follow-up" rules={[{ required: true, message: 'Choose the follow-up it was made in' }]}>
            <FollowUpSearchSelect customerId={customerId} />
          </Form.Item>
        )}
        <Form.Item name="invoices" label="Invoices covered" rules={[{ required: true, type: 'array', min: 1, message: 'Choose at least one invoice' }]}>
          <InvoiceSearchSelect customerId={customerId} />
        </Form.Item>
        <Form.Item name="promisedDate" label="Promised by" rules={[{ required: true, message: 'Say the date they promised' }]}>
          <DatePicker className="w-full" format="D MMM YYYY" allowClear={false} disabledDate={(day) => day.isBefore(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="amount" label="Amount promised" rules={[{ required: true, whitespace: true, message: 'Enter the amount' }]}>
          <Input addonBefore={currency ?? 'Currency'} inputMode="decimal" placeholder="e.g. 150,000" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
